/** Docs (UA): see modules/game/DOCS.md — field geometry, one source for every anchor */

/**
 * Reference layout the art was drawn against (Figma «🎮 Гра — Спіймай бал», 194:2):
 * a 390-wide iPhone 12 screen. Width stays the world's unit forever — the balance
 * is normalized to it (CUP.width, ITEM_RADIUS are fractions of 390). Height is the
 * only thing that moves: `refH` is what the mockups used, not a constraint.
 */
export const FIELD = {
    refW: 390,
    refH: 844,
    canopyH: 124,
    scallopR: 22,
} as const;

/**
 * Інваріант: computeLayout(390, 844, dpr, 110) мусить дати РІВНО ці якорі —
 * ті самі числа, за якими намальовані макети. Будь-яка правка формул нижче
 * перевіряється спершу на ньому, інакше еталонний екран поїде.
 */
export const REFERENCE_ANCHORS = {
    canopyH: 124,
    spawnY: 146,
    catchY: 724,
    puddleY: 760,
} as const;

/**
 * Стеля масштабу: без неї повноекранний десктоп (1440px) роздув би світ у 3.7×
 * — гігантські спрайти, розмиті на запечених текстурах. 1.25 → сцена максимум 488px.
 */
const MAX_CSS_SCALE = 1.25;
/** Нижче цієї висоти світу коридор падіння коротший за половину еталонного. */
const MIN_WORLD_H = 560;
/** Вужча колонка — грати вже неможливо: спрайт стає менший за подушечку пальця. */
const MIN_STAGE_W = 280;

/** Підлога: 84 з 844 на еталоні; межі тримають її смугою, а не тонкою рискою чи полем. */
const FLOOR_RATIO = 84 / 844;
const FLOOR_MIN = 76;
const FLOOR_MAX = 104;
/** Ловець стоїть на 36 над лінією підлоги, предмети виходять на 22 з-під козирка. */
const CATCH_ABOVE_FLOOR = 36;
const SPAWN_BELOW_CANOPY = 22;
/** Проміжок між низом HUD і краєм козирка. Рівно 2, бо стільки їх у макеті:
 *  HUD там 122 css px, козирок 124. Більше — і козирок на еталонному екрані
 *  став би вищим за намальований. */
const HUD_CLEARANCE = 2;

export interface FieldLayout {
    /** css px на одну ref-одиницю. */
    cssScale: number;
    /** device px на одну ref-одиницю — цим масштабується контекст canvas. */
    scale: number;
    /** Щільність пікселів, з якою пораховано scale — потрібна для зсуву сцени. */
    dpr: number;
    /** css px зліва до сцени: 0, поки світ заповнює ширину; >0 при леттербоксі. */
    offsetX: number;
    /** Ширина сцени в css px. */
    stageW: number;
    /** Висота світу в ref-одиницях — ЄДИНА змінна геометрії. */
    worldH: number;
    canopyH: number;
    spawnY: number;
    catchY: number;
    puddleY: number;
    /** false — поле стиснуте так, що гра нечесна: показуємо картку повороту. */
    playable: boolean;
}

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

/**
 * Розкладка поля під реальну коробку. Ширина світу незмінна (баланс), висота
 * дорівнює тому, що дав браузер, а підлога прибита до низу — тому ловець і жар
 * видно на будь-якому екрані, включно з iPhone 12, де адресний рядок Safari
 * забирає ті самі 180px, у яких вони й жили.
 *
 * `hudCssH` — виміряна висота DOM-HUD: козирок під нього підганяється, бо HUD
 * лишається продуктового розміру й не масштабується разом зі світом.
 */
export const computeLayout = (
    cssW: number,
    cssH: number,
    dpr: number,
    hudCssH: number,
): FieldLayout => {
    let cssScale = Math.min(cssW / FIELD.refW, MAX_CSS_SCALE);
    let worldH = cssH / cssScale;
    if (worldH < MIN_WORLD_H) {
        // Низьке вікно або ландшафт: фітимось по висоті, світ звужується в колонку.
        cssScale = cssH / MIN_WORLD_H;
        worldH = MIN_WORLD_H;
    }

    const stageW = FIELD.refW * cssScale;
    const canopyH = Math.max(FIELD.canopyH, (hudCssH + HUD_CLEARANCE) / cssScale);
    const puddleY = worldH - clamp(worldH * FLOOR_RATIO, FLOOR_MIN, FLOOR_MAX);

    return {
        cssScale,
        scale: cssScale * dpr,
        dpr,
        offsetX: (cssW - stageW) / 2,
        stageW,
        worldH,
        canopyH,
        spawnY: canopyH + SPAWN_BELOW_CANOPY,
        catchY: puddleY - CATCH_ABOVE_FLOOR,
        puddleY,
        playable: stageW >= MIN_STAGE_W,
    };
};
